import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import {
  CssBaseline,
  Box,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Typography,
  Button,
  Tooltip,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  IconButton,
  Popover,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGridPro } from '@mui/x-data-grid-pro';
import '../App.css';

// Deeplink supported sportsbooks
const DEEPLINK_SUPPORTED_BOOKS = [
  'fanduel',
  'draftkings',
  'betmgm',
  'caesars',
  'espn bet', 
  'hard rock',
  'novig',
  'fanatics',
  'prophet exchange'
];

// Static list of leagues
const STATIC_LEAGUES = [
  'NBA',
  'CFB',
  'NFL'
];

// Static list of books
const STATIC_BOOKS = [
  'DraftKings',
  'Pinnacle',
  'Bookmaker',
  'FanDuel',
  'ESPN BET',
  'Caesars',
  'BetMGM',
  'BetRivers',  
  'Builder',
  'Bet365',
  'Bovada',
  'Fliff',
  'Hard Rock',
  'Fanatics',
  'Prophet Exchange',
  'NoVig',
];

// Custom dark theme
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: { fontFamily: 'Roboto, Arial, sans-serif', h5: { fontWeight: 600 } },
  components: {
    MuiCheckbox: {
      styleOverrides: { root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } } },
    },
    MuiInputLabel: { styleOverrides: { root: { color: '#ffffff' } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
        },
      },
    },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: '#333333', color: '#ffffff' } } },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

// DeeplinkCell component
const DeeplinkCell = React.memo(({ marketLineId, points, marketSourceName, fetchDeeplink, isAuthenticated }) => {
  const [loading, setLoading] = useState(false);
  
  const bookSupportsDeeplink = DEEPLINK_SUPPORTED_BOOKS.includes(
    marketSourceName?.toLowerCase()
  );
  
  const handleBetClick = async () => {
    if (!isAuthenticated || !marketLineId || !bookSupportsDeeplink) {
      return;
    }
    
    setLoading(true);
    
    try {
      const deeplink = await fetchDeeplink(marketLineId, points, marketSourceName);
      
      if (deeplink) {
        window.open(deeplink, '_blank', 'width=1000,height=800,scrollbars=yes');
      }
    } catch (error) {
      console.error('Error fetching deeplink:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const disabled = !marketLineId || !isAuthenticated || !bookSupportsDeeplink || loading;
  
  return (
    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <Button
        onClick={handleBetClick}
        disabled={disabled}
        size="small"
        sx={{
          color: disabled ? '#666666' : '#00ff00',
          backgroundColor: 'transparent',
          border: 'none',
          padding: '4px 8px',
          fontSize: '0.875rem',
          '&.Mui-disabled': { color: '#666666' },
        }}
      >
        {loading ? '...' : 'Bet'}
      </Button>
    </Box>
  );
});

// Hide Middle Cell Component
const HideMiddleCell = React.memo(({ row, hiddenMiddles, toggleHide, showHiddenMiddles, isAuthenticated }) => {
  const id = row.id;
  const isHidden = hiddenMiddles.includes(id);
  const isDisabled = !isAuthenticated;
  
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleHide(row);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isHidden ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={(isHidden && !showHiddenMiddles) || isDisabled}
      >
        {isHidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
      </IconButton>
    </Box>
  );
});

// Track Middle Cell Component
const TrackMiddleCell = React.memo(({ row, trackedBets, toggleTrack, trackBet, untrackBet, trackingInProgress, isAuthenticated }) => {
  const bet1Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === row.oddsId1 || bet.id === row.oddsId1
  );
  const bet2Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === row.oddsId2 || bet.id === row.oddsId2
  );
  const isFullyTracked = bet1Tracked && bet2Tracked;
  
  const isDisabled = !trackBet || !untrackBet || trackingInProgress.has(row.id) || !isAuthenticated;

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
      <IconButton
        onClick={() => {
          if (!isDisabled) {
            toggleTrack(row, trackBet, untrackBet, bet1Tracked, bet2Tracked);
          }
        }}
        size="small"
        sx={{ padding: 0, color: isFullyTracked ? 'green' : 'grey', '&.Mui-disabled': { color: 'grey' } }}
        disabled={isDisabled}
      >
        {isFullyTracked ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
          </svg>
        )}
      </IconButton>
    </Box>
  );
});

// Mobile Middle Card Component
const MobileMiddleCard = React.memo(({ 
  middle, trackedBets, trackBet, untrackBet, trackingInProgress, isAuthenticated, 
  hiddenMiddles, toggleHide, showHiddenMiddles, fetchDeeplink 
}) => {
  const bet1Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === middle.oddsId1 || bet.id === middle.oddsId1
  );
  const bet2Tracked = trackedBets && trackedBets.some(bet => 
    bet.oddsId === middle.oddsId2 || bet.id === middle.oddsId2
  );
  const isTracked = bet1Tracked && bet2Tracked;
  const isTrackingDisabled = !trackBet || !untrackBet || trackingInProgress.has(middle.id) || !isAuthenticated;
  const isHidden = hiddenMiddles.includes(middle.id);
  const isHideDisabled = (isHidden && !showHiddenMiddles) || !isAuthenticated;

  const [awayTeam, homeTeam] = (middle.eventShortName || '').split('@');
  const eventDate = middle.eventStart
    ? new Date(middle.eventStart).toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).replace(',', '')
    : '';
  
  const formatLine = (side, points) => {
    const sideAbbr = side === 'Over' ? 'o' : side === 'Under' ? 'u' : '';
    return `${sideAbbr}${points || ''}`.trim();
  };
  
  const line1 = formatLine(middle.side1, middle.points1);
  const line2 = formatLine(middle.side2, middle.points2);

  const [loadingBet1, setLoadingBet1] = useState(false);
  const [loadingBet2, setLoadingBet2] = useState(false);

  const bookSupportsDeeplink1 = DEEPLINK_SUPPORTED_BOOKS.includes(middle.ms1?.toLowerCase());
  const bookSupportsDeeplink2 = DEEPLINK_SUPPORTED_BOOKS.includes(middle.ms2?.toLowerCase());
  
  return (
    <Card sx={{ 
      mb: 2, 
      border: '1px solid #333',
      backgroundColor: '#1e1e1e',
      borderRadius: 2,
      overflow: 'hidden',
      position: 'relative',
    }}>
      <Box sx={{ 
        backgroundColor: '#2a2a2a',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #333'
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Middle Opportunity
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              size="small"
              onClick={() => {
                if (!isTrackingDisabled) {
                  const toggleTrackFunc = () => {
                    if (isTracked) {
                      if (bet1Tracked && middle.oddsId1) untrackBet(middle.oddsId1);
                      if (bet2Tracked && middle.oddsId2) untrackBet(middle.oddsId2);
                    } else {
                      let sidepoints1 = '';
                      if (middle.side1 === 'Over') sidepoints1 = 'o';
                      else if (middle.side1 === 'Under') sidepoints1 = 'u';
                      else sidepoints1 = middle.side1 || '';
                      sidepoints1 = `${sidepoints1}${middle.points1 || ''}`.trim();

                      let sidepoints2 = '';
                      if (middle.side2 === 'Over') sidepoints2 = 'o';
                      else if (middle.side2 === 'Under') sidepoints2 = 'u';
                      else sidepoints2 = middle.side2 || '';
                      sidepoints2 = `${sidepoints2}${middle.points2 || ''}`.trim();

                      const bet1 = {
                        id: middle.oddsId1 || `${middle.id}_bet1`,
                        oddsId: middle.oddsId1,
                        marketSourceName: middle.ms1,
                        eventShortName: middle.eventShortName,
                        eventStart: middle.eventStart,
                        playerName: middle.playerName,
                        betTypeName: middle.betTypeName,
                        side: middle.side1,
                        points: middle.points1,
                        price: middle.price1,
                        roi: middle.roi1 / 100,
                        marketLineId: middle.marketLineId1,
                        logourl: middle.logo1,
                        awayLogo: middle.awayLogo,
                        homeLogo: middle.homeLogo,
                        leagueName: middle.leagueName,
                        betMarketId: middle.betMarketId,
                        sidepoints: sidepoints1
                      };
                      
                      const bet2 = {
                        id: middle.oddsId2 || `${middle.id}_bet2`,
                        oddsId: middle.oddsId2,
                        marketSourceName: middle.ms2,
                        eventShortName: middle.eventShortName,
                        eventStart: middle.eventStart,
                        playerName: middle.playerName,
                        betTypeName: middle.betTypeName,
                        side: middle.side2,
                        points: middle.points2,
                        price: middle.price2,
                        roi: middle.roi2 / 100,
                        marketLineId: middle.marketLineId2,
                        logourl: middle.logo2,
                        awayLogo: middle.awayLogo,
                        homeLogo: middle.homeLogo,
                        leagueName: middle.leagueName,
                        betMarketId: middle.betMarketId,
                        sidepoints: sidepoints2
                      };
                      
                      if (!bet1Tracked && middle.oddsId1) trackBet(bet1);
                      if (!bet2Tracked && middle.oddsId2) trackBet(bet2);
                    }
                  };
                  toggleTrackFunc();
                }
              }}
              disabled={isTrackingDisabled}
              sx={{ 
                color: isTracked ? '#00ff00' : '#888',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                '&.Mui-disabled': { color: '#444' }
              }}
            >
              {isTracked ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.11 0 2-.9 2-2V5c0-1.1-.89-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
                </svg>
              )}
            </IconButton>
            <IconButton
              size="small"
              onClick={() => !isHideDisabled && toggleHide(middle)}
              disabled={isHideDisabled}
              sx={{ 
                color: isHidden ? '#00ff00' : '#888',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                '&.Mui-disabled': { color: '#444' }
              }}
            >
              {isHidden ? <VisibilityIcon sx={{ fontSize: 20 }} /> : <VisibilityOffIcon sx={{ fontSize: 20 }} />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>
      
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {(middle.playerName || middle.betTypeName) && (
          <Box mb={2}>
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {middle.playerName}
            </Typography>
            <Typography variant="body2" sx={{ color: '#BBBBBB' }}>
              {middle.betTypeName}
            </Typography>
          </Box>
        )}
        
        <Box mb={2}>
          <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
            {middle.leagueName && (
              <>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {middle.leagueName}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  |
                </Typography>
              </>
            )}
            {middle.awayLogo && (
              <img
                src={middle.awayLogo}
                alt={awayTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {awayTeam}
            </Typography>
            <Typography variant="body2" sx={{ mx: 0.5, color: '#ffffff' }}>
              @
            </Typography>
            {middle.homeLogo && (
              <img
                src={middle.homeLogo}
                alt={homeTeam}
                style={{ width: 'auto', height: '16px', objectFit: 'contain' }}
                loading="lazy"
              />
            )}
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff' }}>
              {homeTeam}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: '#BBBBBB' }}>
            {eventDate}
          </Typography>
        </Box>
        
        <Stack spacing={2}>
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={middle.logo1}
                alt={middle.ms1}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  if (middle.marketLineId1 && isAuthenticated && bookSupportsDeeplink1) {
                    setLoadingBet1(true);
                    try {
                      const deeplink = await fetchDeeplink(middle.marketLineId1, middle.points1, middle.ms1);
                      if (deeplink) window.open(deeplink, '_blank');
                    } catch (error) {
                      console.error('Error fetching deeplink:', error);
                    } finally {
                      setLoadingBet1(false);
                    }
                  }
                }}
                disabled={!middle.marketLineId1 || !isAuthenticated || !bookSupportsDeeplink1 || loadingBet1}
                sx={{
                  backgroundColor: bookSupportsDeeplink1 && isAuthenticated ? '#00ff00' : '#333',
                  color: bookSupportsDeeplink1 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: bookSupportsDeeplink1 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                {loadingBet1 ? '...' : 'Bet'}
              </Button>
            </Stack>
            
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line1} {middle.price1 < 0 ? middle.price1 : `+${middle.price1}`}
            </Typography>
            
            <Typography variant="caption" sx={{ color: '#00ff00' }}>
              EV: {middle.roi1.toFixed(1)}%
            </Typography>
          </Box>
          
          <Box sx={{ 
            p: 1.5,
            borderRadius: 1,
            backgroundColor: '#2a2a2a',
            border: '1px solid #444'
          }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <img
                src={middle.logo2}
                alt={middle.ms2}
                style={{ width: 60, height: 30, objectFit: 'contain' }}
                loading="lazy"
              />
              <Button
                variant="contained"
                size="small"
                onClick={async () => {
                  if (middle.marketLineId2 && isAuthenticated && bookSupportsDeeplink2) {
                    setLoadingBet2(true);
                    try {
                      const deeplink = await fetchDeeplink(middle.marketLineId2, middle.points2, middle.ms2);
                      if (deeplink) window.open(deeplink, '_blank');
                    } catch (error) {
                      console.error('Error fetching deeplink:', error);
                    } finally {
                      setLoadingBet2(false);
                    }
                  }
                }}
                disabled={!middle.marketLineId2 || !isAuthenticated || !bookSupportsDeeplink2 || loadingBet2}
                sx={{
                  backgroundColor: bookSupportsDeeplink2 && isAuthenticated ? '#00ff00' : '#333',
                  color: bookSupportsDeeplink2 && isAuthenticated ? '#000' : '#666',
                  minWidth: 70,
                  '&:hover': { backgroundColor: bookSupportsDeeplink2 && isAuthenticated ? '#00cc00' : '#333' },
                  '&.Mui-disabled': { backgroundColor: '#333', color: '#666' }
                }}
              >
                {loadingBet2 ? '...' : 'Bet'}
              </Button>
            </Stack>
            
            <Typography variant="body1" sx={{ fontWeight: 500, color: '#ffffff', mb: 0.5 }}>
              {line2} {middle.price2 < 0 ? middle.price2 : `+${middle.price2}`}
            </Typography>
            
            <Typography variant="caption" sx={{ color: '#00ff00' }}>
              EV: {middle.roi2.toFixed(1)}%
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
});

const MiddlesDataGrid = ({ middles, updatesPaused, trackBet, untrackBet, trackedBets = [], isAuthenticated = true, userState = '' }) => {
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  const fetchDeeplink = useCallback(async (marketLineId, points, marketSourceName) => {
    try {
      const response = await fetch('https://ws.betpros.ai:8080/api/deeplink', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          marketLineId,
          points,
          marketSourceName,
          isMobile: screenWidth < 768,
          userState: userState || ''
        })
      });
      
      const data = await response.json();
           console.log('Deeplink response:', data);
      console.log('Returned URL:', data.url);
      return data.url;
    } catch (error) {
      console.error('Error fetching deeplink:', error);
      return null;
    }
  }, [screenWidth, userState]);
  
  const savedLeagueFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('middleLeagueFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedBookFilter = useMemo(() => {
    try {
      const saved = localStorage.getItem('middleBookFilter');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }, []);

  const savedHideTracked = useMemo(() => {
    try {
      const saved = localStorage.getItem('middleHideTracked');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  }, []);

  const savedShowHiddenMiddles = useMemo(() => {
    try {
      const saved = localStorage.getItem('middleShowHiddenMiddles');
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  }, []);

  const [leagueFilter, setLeagueFilter] = useState(savedLeagueFilter);
  const [bookFilter, setBookFilter] = useState(savedBookFilter);
  const leagueInitialized = useRef(false);
  const bookInitialized = useRef(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [trackingInProgress, setTrackingInProgress] = useState(new Set());
  const [hideTracked, setHideTracked] = useState(savedHideTracked);
  const [showHiddenMiddles, setShowHiddenMiddles] = useState(savedShowHiddenMiddles);
  const [hiddenMiddles, setHiddenMiddles] = useState(() => {
    const saved = localStorage.getItem('hiddenMiddles');
    const parsed = saved ? JSON.parse(saved) : [];
    return parsed.slice(-1000);
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

  const toggleTrack = useCallback((row, trackBetFunc, untrackBetFunc, bet1Tracked, bet2Tracked) => {
    if (!isAuthenticated || !trackBetFunc || !untrackBetFunc) {
      return;
    }
    
    const middleId = row.id;
    
    if (trackingInProgress.has(middleId)) {
      return;
    }

    setTrackingInProgress(prev => new Set(prev).add(middleId));

    if (bet1Tracked && bet2Tracked) {
      if (row.oddsId1) untrackBetFunc(row.oddsId1);
      if (row.oddsId2) untrackBetFunc(row.oddsId2);
    } else {
      let sidepoints1 = '';
      if (row.side1 === 'Over') sidepoints1 = 'o';
      else if (row.side1 === 'Under') sidepoints1 = 'u';
      else sidepoints1 = row.side1 || '';
      sidepoints1 = `${sidepoints1}${row.points1 || ''}`.trim();

      let sidepoints2 = '';
      if (row.side2 === 'Over') sidepoints2 = 'o';
      else if (row.side2 === 'Under') sidepoints2 = 'u';
      else sidepoints2 = row.side2 || '';
      sidepoints2 = `${sidepoints2}${row.points2 || ''}`.trim();

      const bet1 = {
        id: row.oddsId1,
        oddsId: row.oddsId1,
        marketSourceName: row.ms1,
        eventShortName: row.eventShortName,
        eventStart: row.eventStart,
        playerName: row.playerName,
        betTypeName: row.betTypeName,
        side: row.side1,
        points: row.points1,
        price: row.price1,
        roi: row.roi1 / 100,
        marketLineId: row.marketLineId1,
        logourl: row.logo1,
        awayLogo: row.awayLogo,
        homeLogo: row.homeLogo,
        leagueName: row.leagueName,
        betMarketId: row.betMarketId,
        sidepoints: sidepoints1
      };
      
      const bet2 = {
        id: row.oddsId2,
        oddsId: row.oddsId2,
        marketSourceName: row.ms2,
        eventShortName: row.eventShortName,
        eventStart: row.eventStart,
        playerName: row.playerName,
        betTypeName: row.betTypeName,
        side: row.side2,
        points: row.points2,
        price: row.price2,
        roi: row.roi2 / 100,
        marketLineId: row.marketLineId2,
        logourl: row.logo2,
        awayLogo: row.awayLogo,
        homeLogo: row.homeLogo,
        leagueName: row.leagueName,
        betMarketId: row.betMarketId,
        sidepoints: sidepoints2
      };
      
      if (!bet1Tracked && row.oddsId1) {
        trackBetFunc(bet1);
      }
      if (!bet2Tracked && row.oddsId2) {
        trackBetFunc(bet2);
      }
    }

    setTimeout(() => {
      setTrackingInProgress(prev => {
        const next = new Set(prev);
        next.delete(middleId);
        return next;
      });
    }, 500);
  }, [trackedBets, isAuthenticated]);

  const toggleHide = useCallback((row) => {
    if (!isAuthenticated) {
      return;
    }
    
    const id = row.id;
    const isHidden = hiddenMiddles.includes(id);
    
    const updatedHiddenMiddles = isHidden
      ? hiddenMiddles.filter(x => x !== id)
      : [...hiddenMiddles, id];
    
    setHiddenMiddles(updatedHiddenMiddles);
    localStorage.setItem('hiddenMiddles', JSON.stringify(updatedHiddenMiddles));
  }, [hiddenMiddles, isAuthenticated]);

  useEffect(() => {
    localStorage.setItem('middleLeagueFilter', JSON.stringify(leagueFilter));
  }, [leagueFilter]);

  useEffect(() => {
    localStorage.setItem('middleBookFilter', JSON.stringify(bookFilter));
  }, [bookFilter]);

  useEffect(() => {
    localStorage.setItem('middleHideTracked', JSON.stringify(hideTracked));
  }, [hideTracked]);

  useEffect(() => {
    localStorage.setItem('middleShowHiddenMiddles', JSON.stringify(showHiddenMiddles));
  }, [showHiddenMiddles]);

  useEffect(() => {
    if (middles && middles.length > 0) {
      const currentIds = new Set(middles.map(middle => middle.id));
      setHiddenMiddles(prev => {
        const validHiddenMiddles = prev.filter(id => currentIds.has(id));
        return validHiddenMiddles.length !== prev.length ? validHiddenMiddles : prev;
      });
    }
  }, [middles]);

  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSettingsOpen = useCallback((event) => {
    setSettingsAnchorEl(event.currentTarget);
  }, []);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  const { filterOptions, filteredMiddles } = useMemo(() => {
    if (!middles || !middles.length) {
      return {
        filterOptions: { leagues: STATIC_LEAGUES, books: STATIC_BOOKS },
        filteredMiddles: [],
      };
    }

    const leagueSet = new Set(leagueFilter);
    const bookSet = new Set(bookFilter);
    const trackedSet = new Set(trackedBets.map(bet => bet.oddsId || bet.id));
    const hiddenSet = new Set(hiddenMiddles);
    
    const filtered = middles.filter((middle) => {
      if (leagueFilter.length > 0 && middle.leagueName && !leagueSet.has(middle.leagueName)) {
        return false;
      }
      if (bookFilter.length > 0) {
        const matchesBook1 = middle.ms1 && bookSet.has(middle.ms1);
        const matchesBook2 = middle.ms2 && bookSet.has(middle.ms2);
        if (!matchesBook1 || !matchesBook2) {
          return false;
        }
      }
      if (hideTracked) {
        const bet1Tracked = trackedSet.has(middle.oddsId1);
        const bet2Tracked = trackedSet.has(middle.oddsId2);
        if (bet1Tracked && bet2Tracked) {
          return false;
        }
      }
      if (!showHiddenMiddles && hiddenSet.has(middle.id)) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      return b.avgEVPercent - a.avgEVPercent;
    });

    return {
      filterOptions: {
        leagues: STATIC_LEAGUES,
        books: STATIC_BOOKS,
      },
      filteredMiddles: sorted,
    };
  }, [middles, leagueFilter, bookFilter, hideTracked, trackedBets, hiddenMiddles, showHiddenMiddles]);

  useEffect(() => {
    if (STATIC_LEAGUES.length > 0 && !leagueInitialized.current) {
      if (savedLeagueFilter.length > 0) {
        const validSavedFilters = savedLeagueFilter.filter(league => 
          STATIC_LEAGUES.includes(league)
        );
        if (validSavedFilters.length > 0) {
          setLeagueFilter(validSavedFilters);
        } else {
          setLeagueFilter(STATIC_LEAGUES);
        }
      } else {
        setLeagueFilter(STATIC_LEAGUES);
      }
      leagueInitialized.current = true;
    }
  }, [savedLeagueFilter]);

  useEffect(() => {
    if (STATIC_BOOKS.length > 0 && !bookInitialized.current) {
      if (savedBookFilter.length > 0) {
        const validSavedFilters = savedBookFilter.filter(book => 
          STATIC_BOOKS.includes(book)
        );
        if (validSavedFilters.length >= 2) {
          setBookFilter(validSavedFilters);
        } else if (validSavedFilters.length === 1) {
          const additionalBook = STATIC_BOOKS.find(book => 
            !validSavedFilters.includes(book)
          );
          setBookFilter([...validSavedFilters, additionalBook]);
        } else {
          setBookFilter(STATIC_BOOKS.slice(0, 2));
        }
      } else {
        setBookFilter(STATIC_BOOKS);
      }
      bookInitialized.current = true;
    }
  }, [savedBookFilter]);

  const handleFilterChange = (e, options, setter, type) => {
    const val = e.target.value;
    if (type === 'book' && val.length < 2) {
      if (val.length === 0) {
        setter(options.slice(0, 2));
      } else if (val.length === 1) {
        const remainingOptions = options.filter(opt => !val.includes(opt));
        setter([...val, remainingOptions[0]]);
      }
      return;
    }

    if (val[val.length - 1] === 'all') {
      setter(prev => {
        const newValue = prev.length === options.length ? [] : options;
        if (type === 'book' && newValue.length < 2) {
          const enforcedValue = options.slice(0, 2);
          return enforcedValue;
        }
        return newValue;
      });
    } else {
      setter(val);
    }
  };

  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple selections (${sel.length})` : sel[0];
  };

  const mobilePageSize = 5;
  const totalPages = Math.ceil(filteredMiddles.length / mobilePageSize);
  const startIdx = currentPage * mobilePageSize;
  const endIdx = startIdx + mobilePageSize;
  const paginatedMiddles = filteredMiddles.slice(startIdx, endIdx);

  useEffect(() => {
    setCurrentPage(0);
  }, [leagueFilter, bookFilter, hideTracked, showHiddenMiddles]);

  const columns = [
    {
      field: 'eventShortName',
      headerName: 'Event',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '14px', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '12px', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '14px' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      minWidth: 165,
      flex: 2,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '14px',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'logo1',
      headerName: 'Book',
      minWidth: 90,
      flex: 0.1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingTop: '2px',
            }}
          >
            <img
              src={params.value}
              alt="Logo 1"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
              height: '50%',
              paddingBottom: '2px',
            }}
          >
            <img
              src={params.row.logo2}
              alt="Logo 2"
              style={{ width: 72, height: 36, objectFit: 'contain' }}
              loading="lazy"
            />
          </div>
        </Box>
      ),
    },
    {
      field: 'lineOdds',
      headerName: 'Line/Odds',
      minWidth: 130,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'right',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingRight: '8px',
          }}
        >
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side1 === 'Over' ? 'o' : params.row.side1 === 'Under' ? 'u' : ''} ${params.row.points1 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price1 < 0 ? params.row.price1 : `+${params.row.price1}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Box sx={{ minWidth: '60px', textAlign: 'right', mr: 0.5 }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {`${params.row.side2 === 'Over' ? 'o' : params.row.side2 === 'Under' ? 'u' : ''} ${params.row.points2 || ''}`.trim()}
                </Typography>
              </Box>
              <Box sx={{ minWidth: '40px', textAlign: 'right' }}>
                <Typography variant="body2" sx={{ color: '#ffffff' }}>
                  {params.row.price2 < 0 ? params.row.price2 : `+${params.row.price2}`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'ev',
      headerName: 'EV',
      minWidth: 100,
      flex: 1,
      sortable: false,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingLeft: '8px',
          }}
        >
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px' }}>
            <Typography variant="body2" sx={{ color: '#00ff00' }}>
              {params.row.roi1.toFixed(1)}%
            </Typography>
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px' }}>
            <Typography variant="body2" sx={{ color: '#00ff00' }}>
              {params.row.roi2.toFixed(1)}%
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'trackMiddle',
      headerName: 'Track',
      sortable: false,
      minWidth: 65,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <TrackMiddleCell
          row={params.row}
          trackedBets={trackedBets}
          toggleTrack={toggleTrack}
          trackBet={trackBet}
          untrackBet={untrackBet}
          trackingInProgress={trackingInProgress}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'hideMiddle',
      headerName: 'Hide',
      sortable: false,
      minWidth: 65,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <HideMiddleCell
          row={params.row}
          hiddenMiddles={hiddenMiddles}
          toggleHide={toggleHide}
          showHiddenMiddles={showHiddenMiddles}
          isAuthenticated={isAuthenticated}
        />
      )
    },
    {
      field: 'deeplink1',
      headerName: '',
      sortable: false,
      minWidth: 80,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
      renderCell: params => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingTop: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell 
              marketLineId={params.row.marketLineId1}
              points={params.row.points1}
              marketSourceName={params.row.ms1}
              fetchDeeplink={fetchDeeplink}
              isAuthenticated={isAuthenticated}
            />
          </Box>
          <Box sx={{ height: '50%', display: 'flex', alignItems: 'center', paddingBottom: '2px', paddingLeft: '8px' }}>
            <DeeplinkCell 
              marketLineId={params.row.marketLineId2}
              points={params.row.points2}
              marketSourceName={params.row.ms2}
              fetchDeeplink={fetchDeeplink}
              isAuthenticated={isAuthenticated}
            />
          </Box>
        </Box>
      ),
    },
  ];

  const columnVisibilityModel = {};

  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          
          {isMobile ? (
            <>
              <Box sx={{ 
                position: 'sticky',
                top: 0,
                backgroundColor: '#121212',
                px: 2,
                py: 1.5,
                borderBottom: '1px solid #333',
                zIndex: 10
              }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        sx={{ fontSize: '0.875rem' }}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ flex: 1, minWidth: 100 }}>
                      <InputLabel id="mobile-book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="mobile-book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        sx={{ fontSize: '0.875rem' }}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton 
                      onClick={handleSettingsOpen} 
                      disabled={!isAuthenticated}
                      sx={{ 
                        color: '#ffffff', 
                        '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
                      }}
                    >
                      <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>

              <Box sx={{ 
                flex: 1,
                overflowY: 'auto',
                p: 2,
                paddingBottom: totalPages > 1 ? '80px' : '16px'
              }}>
                {filteredMiddles.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
                    <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                      No middle opportunities available
                    </Typography>
                  </Box>
                ) : (
                  paginatedMiddles.map(middle => (
                    <MobileMiddleCard 
                      key={middle.id} 
                      middle={middle}
                      trackedBets={trackedBets}
                      trackBet={trackBet}
                      untrackBet={untrackBet}
                      trackingInProgress={trackingInProgress}
                      isAuthenticated={isAuthenticated}
                      hiddenMiddles={hiddenMiddles}
                      toggleHide={toggleHide}
                      showHiddenMiddles={showHiddenMiddles}
                      fetchDeeplink={fetchDeeplink}
                    />
                  ))
                )}
              </Box>

              {totalPages > 1 && (
                <Box sx={{ 
                  position: 'fixed',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: '#1e1e1e',
                  borderTop: '1px solid #333',
                  p: 2,
                  zIndex: 9
                }}>
                  <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0 || !isAuthenticated}
                      sx={{ color: '#ffffff' }}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ color: '#ffffff' }}>
                      {currentPage + 1} / {totalPages}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                      disabled={currentPage >= totalPages - 1 || !isAuthenticated}
                      sx={{ color: '#ffffff' }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box className="filter-section">
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={2}>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="league-filter-label" sx={{ color: '#ffffff' }}>League</InputLabel>
                      <Select
                        multiple
                        labelId="league-filter-label"
                        label="League"
                        notched
                        value={leagueFilter}
                        onChange={e => handleFilterChange(e, filterOptions.leagues, setLeagueFilter, 'league')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.leagues, 'League')}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={leagueFilter.length === filterOptions.leagues.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.leagues.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={leagueFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                      <InputLabel id="book-filter-label" sx={{ color: '#ffffff' }}>Book</InputLabel>
                      <Select
                        multiple
                        labelId="book-filter-label"
                        label="Book"
                        notched
                        value={bookFilter}
                        onChange={e => handleFilterChange(e, filterOptions.books, setBookFilter, 'book')}
                        renderValue={sel => renderFilterValue(sel, filterOptions.books, 'Book')}
                        disabled={!isAuthenticated}
                      >
                        <MenuItem value="all">
                          <Checkbox checked={bookFilter.length === filterOptions.books.length} />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {filterOptions.books.map(o => (
                          <MenuItem key={o} value={o}>
                            <Checkbox checked={bookFilter.includes(o)} />
                            <ListItemText primary={o} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                  <IconButton 
                    onClick={handleSettingsOpen} 
                    disabled={!isAuthenticated}
                    sx={{ 
                      color: '#ffffff', 
                      '&.Mui-disabled': { color: '#ffffff', opacity: 0.5 },
                    }}
                  >
                    <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                  </IconButton>
                </Stack>
              </Box>
              <Box className="table-section">
                <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
                  {filteredMiddles.length === 0 ? (
                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ color: '#ffffff', fontSize: '1rem' }}>
                        No middle opportunities available
                      </Typography>
                    </Box>
                  ) : (
                    <DataGridPro
                      rows={filteredMiddles}
                      columns={columns}
                     getRowId={row => `${row.oddsId1}_${row.oddsId2}`}
                      sortingMode="client"
                      disableSelectionOnClick
                      disableColumnMenu
                      hideFooter
                      columnVisibilityModel={columnVisibilityModel}
                      sx={{
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#1e1e1e',
                          borderBottom: '2px solid #444',
                          position: 'sticky',
                          top: '56px',
                          zIndex: 2,
                        },
                        '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-cell': {
                          color: '#ffffff !important',
                          userSelect: 'none',
                          borderRight: '1px solid rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-cell:last-child': {
                          borderRight: 'none',
                        },
                        '& .MuiDataGrid-cell:focus': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-cell:focus-within': {
                          outline: 'none',
                        },
                        '& .MuiDataGrid-row': {
                          position: 'relative',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-row.Mui-selected': {
                          backgroundColor: '#1a1a1a',
                          '&:hover': { backgroundColor: '#1a1a1a' },
                        },
                        '& .MuiDataGrid-columnSeparator': { 
                          visibility: 'visible',
                          color: 'rgba(255, 255, 255, 0.3)',
                        },
                        '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: '#ffffff !important',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          lineHeight: 1.2,
                        },
                        '& .MuiDataGrid-root': {
                          overflowY: 'auto !important',
                          margin: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                          overflowY: 'auto !important',
                        },
                        '& .MuiDataGrid-main': {
                          padding: 0,
                          boxSizing: 'border-box',
                        },
                        '& .MuiDataGrid-footerContainer': {
                          backgroundColor: '#1e1e1e',
                          color: '#ffffff !important',
                          position: 'sticky',
                          bottom: 0,
                          zIndex: 1,
                          boxSizing: 'border-box',
                        },
                      }}
                      rowBuffer={5}
                      rowThreshold={2}
                    />
                  )}
                </Box>
              </Box>
            </>
          )}

          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            sx={{ zIndex: 1300 }}
          >
            <Box sx={{ p: 2, minWidth: 200 }}>
              <Stack direction="column" spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideTracked}
                      onChange={(e) => setHideTracked(e.target.checked)}
                      sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                      disabled={!isAuthenticated}
                    />
                  }
                  label="Hide Tracked Bets"
                  sx={{ color: '#ffffff' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showHiddenMiddles}
                      onChange={(e) => setShowHiddenMiddles(e.target.checked)}
                      sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                      disabled={!isAuthenticated}
                    />
                  }
                  label="Show Hidden Middles"
                  sx={{ color: '#ffffff', mb: 2 }}
                />
              </Stack>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={handleSettingsClose}
                  sx={{ color: '#ffffff' }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  onClick={handleSettingsClose}
                  variant="contained"
                  sx={{ backgroundColor: '#ff1744', '&:hover': { backgroundColor: '#d81b60' } }}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Popover>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
};

export default MiddlesDataGrid;